import React, { useState, useRef } from 'react';
import Media, { isImage, isVideo, getDimensionFormatted, getThumbnail } from '../../models/Media';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faImage, faVideo, faCheck } from '@fortawesome/free-solid-svg-icons';
import RenderUser from '../RenderUser';
import useOwnerResource from '../../hooks/useOwnerResource';

interface MediaItemProps {
    media: Media;
    onSelect?: (media: Media) => void;
    visibleCheck?: boolean;
    isChecked?: boolean;
    onChecked?: (media: Media) => void;
    enableAutoPlayOnHover?: boolean;
    clipVideo?: boolean;
    silent?: boolean;
};

const MediaItem: React.FC<MediaItemProps> = (props: MediaItemProps) => {

    const [t] = useTranslation();

    const media: Media = props.media;
    const [isPlaying, setIsPlaying] = useState<boolean>(false);

    const videoEl = useRef<any>(null);
    const isEnableAutoPlayOnHover: boolean = props.enableAutoPlayOnHover ?? false;
    const isSilent: boolean = props.silent ?? false;
    const isBatchMode: boolean = props.visibleCheck ?? false;
    const isChecked: boolean = props.isChecked ?? false;

    const ownerResource = useOwnerResource({ item: media });
    const isDifferentUser: boolean = ownerResource.isDifferentUser();

    const _renderResource = () => {

        const url = getThumbnail(props.media, "system_hp_printer");

        if (isImage(media.mime_type)) {
            return (
                <img
                    src={url}
                    alt={props.media.name}
                />
            );
        }

        if (isVideo(media.mime_type)) {
            return (
                <div
                    onMouseOver={() => { if (isEnableAutoPlayOnHover) setIsPlaying(true); }}
                    onMouseOut={() => {
                        if (isEnableAutoPlayOnHover) {
                            setIsPlaying(false);
                            videoEl.current?.seekTo(0);
                        }
                    }}
                >
                    <ReactPlayer
                        ref={videoEl}
                        url={url}
                        playing={isPlaying}
                        volume={isSilent ? 0 : 1}
                        width="100%"
                        height="180px"
                    />
                </div>
            );
        }

        return null;
    }

    const _renderTypeBadge = () => {
        if (isImage(media.mime_type)) {
            return (
                <div className="media-item__type-badge">
                    <FontAwesomeIcon icon={faImage} />
                    <span>img</span>
                </div>
            );
        }
        if (isVideo(media.mime_type)) {
            return (
                <div className="media-item__type-badge">
                    <FontAwesomeIcon icon={faVideo} />
                    <span>vid</span>
                </div>
            );
        }
        return null;
    }

    const _renderHoverOverlay = () => {
        if (isBatchMode) return null;
        return (
            <div
                className="media-item__overlay"
                onClick={() => props.onSelect?.(props.media)}
            >
                <FontAwesomeIcon icon={faEye} size="2x" />
            </div>
        );
    }

    const _renderOwnerBadge = () => {
        if (!isDifferentUser) return null;
        const initial = media.user_name?.charAt(0)?.toUpperCase() ?? '?';
        return (
            <div
                className="media-item__owner-badge"
                title={media.user_name}
            >
                {initial}
            </div>
        );
    }

    const _renderSelectionOverlay = () => {
        if (!isBatchMode) return null;
        return (
            <div
                className={`media-item__select-overlay ${isChecked ? 'is-checked' : 'is-unchecked'}`}
                onClick={() => props.onChecked?.(media)}
            >
                <div className="media-item__select-indicator">
                    {isChecked && <FontAwesomeIcon icon={faCheck} />}
                </div>
            </div>
        );
    }

    const _renderDimension = () => {
        const dimensionText: string | null = getDimensionFormatted(props.media);
        if (!dimensionText) return null;
        return (
            <p className="media-item__meta mb-0">{t('mediaitem_dimension')}: {dimensionText}</p>
        );
    }

    return (
        <div className="media-item card">
            <div className="media-item__thumbnail">
                {_renderResource()}
                {_renderTypeBadge()}
                {_renderOwnerBadge()}
                {_renderHoverOverlay()}
                {_renderSelectionOverlay()}
            </div>
            <div className="card-body">
                <p
                    className="media-item__name"
                    title={props.media.original_name}
                    onClick={() => !isBatchMode && props.onSelect?.(props.media)}
                >
                    {props.media.name}
                </p>
                <p className="media-item__meta mb-0">{t('mediaitem_size')}: {props.media?.size_str ?? ""}</p>
                {_renderDimension()}
                <RenderUser
                    item={props.media}
                    subtitle={true}
                />
            </div>
        </div>
    );

};

export default React.memo(MediaItem);
